import { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardProps,
  CardHeader,
  CardContent,
  Stack,
  Alert,
  ListItem,
  List,
  Typography,
  useTheme
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Device, Project, RuuviTag, Spine, User, UserAlertRule, Valve } from '../types'
import { AlertGroup, AlertSensor, AlertTemplate } from '../types/Alerts'
import { useDispatch } from 'react-redux'
import {
  useAuth,
  useDeviceAlertRules,
  usePolicy,
  useProjectAlertRules,
  useRuuviTagAlertRules,
  useValveAlertRules
} from '../hooks'
import { DeviceType } from '../enums'
import AlertTemplateDefaultsButton from './AlertTemplateDefaultsButton'
import LoadingState from './LoadingState'
import UserEmailNotificationsDisabledWarningAlert from './UserEmailNotificationsDisabledWarningAlert'
import UserSmsNotificationsDisabledWarningAlert from './UserSmsNotificationsDisabledWarning'
import UserAppNotificationsDisabledWarningAlert from './UserAppNotificationsDisabledWarningAlert'
import AlertRuleAddButton from './AlertRuleAddButton'
import AlertRuleDataGrid from './AlertRuleDataGrid'
import { getProjectAlertRules } from '../redux/slices/projects'
import { loadDeviceAlertRules } from '../redux/slices/devices'
import { loadRuuviTagAlertRules } from '../redux/slices/ruuviTag'
import { getAlertTemplates } from '../services/projectService'
import { getDeviceAlertTemplates, getProjectDevices } from '../services/deviceService'
import { getRuuviTagAlertTemplates, getRuuviTags } from '../services/ruuviTagService'
import RuuviTagAlertRuleAccordion from './RuuviTagAlertRuleAccordion'
import { getValveAlertTemplates } from '../services/valve'
import { AxiosRequestConfig } from 'axios'
import ValveAlertRuleAccordion from './ValveAlertRuleAccordion'
import { loadValveAlertRules } from '../redux/slices/valve'
import { isSpine, isValve, isNotVirtualDevice } from '../utils/device'
import { deepMergeAlertGroups } from '../utils/alert'
import DeviceAlertRuleAccordion from './DeviceAlertRuleAccordion'
import { setAlertTemplates as setDeviceAlertTemplates } from '../redux/slices/devices'
import { setAlertTemplates as setValveAlertTemplates } from '../redux/slices/valve'
import { setAlertTemplates as setRuuviTagAlertTemplates } from '../redux/slices/ruuviTag'
import {
  getUserAlertRulesByDevice,
  getUserAlertRulesByProject,
  getUserAlertRulesByRuuviTag
} from '../services/userService'
import { setUserAlertRules } from '../redux/slices/users'

interface AlertRuleManagementCardProps extends Omit<CardProps, 'children'> {
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  user?: User
  useRedux?: boolean
  showSpines?: boolean
  showValves?: boolean
  showRuuviTags?: boolean
}

export default function AlertRuleManagementCard({
  project,
  device,
  ruuviTag,
  user: initialUser,
  useRedux = true,
  showSpines = false,
  showValves = false,
  showRuuviTags = false,
  ...props
}: AlertRuleManagementCardProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The user.
   */
  const user = initialUser || currentUser

  /**
   * The policy state.
   */
  const { canEditProjectAlertRules, canEditDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the current user can edit alert rules.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The alert templates.
   */
  const [alertTemplates, setAlertTemplates] = useState<AlertTemplate[]>([])

  /**
   * The ruuvi tags.
   */
  const [ruuviTags, setRuuviTags] = useState<RuuviTag[]>([])

  /**
   * The spines.
   */
  const [spines, setSpines] = useState<Spine[]>([])

  /**
   * The valves.
   */
  const [valves, setValves] = useState<Valve[]>([])

  /**
   * The project alert rules.
   */
  const projectAlertRules = useProjectAlertRules()

  /**
   * The device alert rules.
   */
  const deviceAlertRules = useDeviceAlertRules()

  /**
   * The valve alert rules.
   */
  const valveAlertRules = useValveAlertRules()

  /**
   * The ruuvitag alert rules.
   */
  const ruuviTagAlertRules = useRuuviTagAlertRules()

  /**
   * The alert rules.
   */
  const alertRules = useMemo(() => {
    let alertRules: AlertGroup[] = []

    if (device) {
      alertRules = alertRules.concat(deviceAlertRules)

      if (device.type === DeviceType.VALVE) {
        alertRules = alertRules.concat(valveAlertRules)
      }
    } else if (ruuviTag) {
      return ruuviTagAlertRules
    }

    if (project) {
      const sensor = device
        ? device.type === DeviceType.SPINE
          ? AlertSensor.DEVICE
          : AlertSensor.VALVE
        : undefined

      alertRules = deepMergeAlertGroups(projectAlertRules, alertRules, sensor)
    }

    return alertRules
  }, [
    project,
    device,
    ruuviTag,
    deviceAlertRules,
    valveAlertRules,
    ruuviTagAlertRules,
    projectAlertRules
  ])

  /**
   * Enabled alert rules.
   */
  const enabledAlertRules = alertRules?.filter((group) => group.alerts.some((rule) => rule.enabled))

  /**
   * Indicates whether email notifications are enabled for the current user.
   */
  const emailNotificationsEnabled = Boolean(currentUser?.notificationsByEmail)

  /**
   * Indicates whether SMS notifications are enabled for the current user.
   */
  const smsNotificationsEnabled = Boolean(currentUser?.notificationsBySms)

  /**
   * Indicates if the asset is without a project.
   */
  const hasNoProject = !device?.asset?.projectId && !ruuviTag?.asset?.projectId

  /**
   * Indicates if the app notifications have been enabled for the project.
   */
  const hasEnabledProjectAppNotifications = Boolean(
    currentUser?.projects?.find(
      (membership) =>
        membership.projectId === project?.id ||
        membership?.projectId === device?.asset?.projectId ||
        membership?.projectId === ruuviTag?.asset?.projectId
    )?.showInAppNotifications
  )

  /**
   * Indicates whether app notifications are enabled for the current user.
   */
  const appNotificationsEnabled =
    currentUser &&
    currentUser.notificationsInApp &&
    (hasNoProject || hasEnabledProjectAppNotifications)

  /**
   * Load alert rule templates.
   */
  async function loadAlertTemplates(): Promise<void> {
    try {
      setLoading(true)

      let alertTemplates: AlertTemplate[] = []

      // Load all the alert templates.
      const [
        projectAlertTemplates,
        deviceAlertTemplates,
        valveAlertTemplates,
        ruuviTagAlertTemplates
      ] = await Promise.all([
        getAlertTemplates(),
        getDeviceAlertTemplates(),
        getValveAlertTemplates(),
        getRuuviTagAlertTemplates()
      ])

      // Store alert templates to redux.
      dispatch(setDeviceAlertTemplates(deviceAlertTemplates))
      dispatch(setValveAlertTemplates(valveAlertTemplates))
      dispatch(setRuuviTagAlertTemplates(ruuviTagAlertTemplates))

      if (device) {
        alertTemplates = alertTemplates.concat(deviceAlertTemplates)

        if (device.type === DeviceType.VALVE) {
          alertTemplates = alertTemplates.concat(valveAlertTemplates)
        }
      } else if (ruuviTag) {
        alertTemplates = alertTemplates.concat(ruuviTagAlertTemplates)
      }

      if (project) {
        alertTemplates = alertTemplates.concat(projectAlertTemplates)
      }

      setAlertTemplates(alertTemplates)
    } catch (_error) {
      // Ignore the error for now.
    } finally {
      setLoading(false)
    }
  }

  async function loadSpinesByProject(projectId: number, config: AxiosRequestConfig = {}) {
    try {
      // FIX: Add support for providing the device type as query.
      const spines = (await getProjectDevices(projectId, config))
        .filter(isSpine)
        .filter(isNotVirtualDevice)
      setSpines(spines)
    } catch (_) {
      // Ignore the error.
    }
  }

  async function loadValvesByProject(projectId: number, config: AxiosRequestConfig = {}) {
    try {
      // FIX: Add support for providing the device type as query.
      const valves = (await getProjectDevices(projectId, config))
        .filter(isValve)
        .filter(isNotVirtualDevice)
      setValves(valves)
    } catch (_) {
      // Ignore the error.
    }
  }

  async function loadRuuviTagsByProject(projectId: number, config: AxiosRequestConfig = {}) {
    try {
      setRuuviTags(await getRuuviTags({ projectId }, config))
    } catch (_) {
      // Ignore the error for now.
    }
  }

  /**
   * Load the user alert rules.
   */
  async function loadUserAlertRules(userId: number): Promise<void> {
    let userAlertRules: UserAlertRule[] = []

    if (project) {
      userAlertRules = userAlertRules.concat(await getUserAlertRulesByProject(userId, project.id))
    }

    if (device) {
      userAlertRules = userAlertRules.concat(await getUserAlertRulesByDevice(userId, device.name))
    }

    if (ruuviTag) {
      userAlertRules = userAlertRules.concat(await getUserAlertRulesByRuuviTag(userId, ruuviTag.id))
    }

    dispatch(setUserAlertRules(userAlertRules))
  }

  /**
   * Load alert templates when component mounts.
   */
  useEffect(() => {
    loadAlertTemplates()
  }, [])

  /**
   * Load the project alert rules when the project changes.
   */
  useEffect(() => {
    if (project?.id) {
      const controller = new AbortController()

      dispatch(getProjectAlertRules(project.id))

      if (showSpines) {
        loadSpinesByProject(project.id, { signal: controller.signal })
      }

      if (showValves) {
        loadValvesByProject(project.id, { signal: controller.signal })
      }

      if (showRuuviTags) {
        loadRuuviTagsByProject(project.id, { signal: controller.signal })
      }

      return () => controller.abort()
    }
  }, [project?.id])

  /**
   * Load the device alert rules when the device chages.
   */
  useEffect(() => {
    if (device?.name) {
      const controller = new AbortController()
      if (device.type === DeviceType.VALVE) {
        dispatch(loadValveAlertRules(device.name, { signal: controller.signal }))
      }

      dispatch(loadDeviceAlertRules(device.name, { signal: controller.signal }))

      return () => controller.abort()
    }
  }, [device?.name])

  /**
   * Load the ruuvi tag alert rules when the ruuvi tag changes.
   */
  useEffect(() => {
    if (ruuviTag?.id) {
      const controller = new AbortController()

      dispatch(loadRuuviTagAlertRules(ruuviTag.id, { signal: controller.signal }))

      return () => controller.abort()
    }
  }, [ruuviTag?.id])

  /**
   * Load the user alert rules when the current user changes.
   */
  useEffect(() => {
    if (user?.id) {
      loadUserAlertRules(user.id)
    }
  }, [user?.id, project?.id, device?.name, ruuviTag?.id])

  return (
    <Card {...props}>
      <CardHeader
        title={t('alertRules')}
        action={
          <Stack direction="row" alignItems="center" gap={2}>
            {canEditAlertRules && (
              <AlertTemplateDefaultsButton
                alertRules={alertRules}
                alertTemplates={alertTemplates}
                project={project}
                device={device}
                ruuviTag={ruuviTag}
                useRedux={useRedux}
              />
            )}

            {canEditAlertRules && (
              <AlertRuleAddButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                project={project}
                device={device}
                ruuviTag={ruuviTag}
                useRedux={useRedux}
              />
            )}
          </Stack>
        }
      />
      <CardContent>
        {loading || !user ? (
          <LoadingState />
        ) : enabledAlertRules.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : (
          <>
            {user && !emailNotificationsEnabled && (
              <UserEmailNotificationsDisabledWarningAlert user={user} sx={{ mb: 6 }} />
            )}

            {user && !smsNotificationsEnabled && (
              <UserSmsNotificationsDisabledWarningAlert user={user} sx={{ mb: 6 }} />
            )}

            {user && !appNotificationsEnabled && (
              <UserAppNotificationsDisabledWarningAlert
                user={user}
                project={project || device?.asset?.project || ruuviTag?.asset?.project}
                sx={{ mb: 6 }}
              />
            )}

            {project ? (
              <>
                <AlertRuleDataGrid
                  alertRules={alertRules}
                  alertTemplates={alertTemplates}
                  project={project}
                  device={device}
                  ruuviTag={ruuviTag}
                  user={user}
                />

                {showSpines && spines.length > 0 && (
                  <>
                    <Typography
                      fontWeight={500}
                      color={theme.palette.text.secondary}
                      sx={{ my: 2, ml: 2 }}
                    >
                      {t('spineDevices')}
                    </Typography>

                    <List disablePadding sx={{ p: 0, m: 0 }}>
                      {spines.map((spine) => (
                        <ListItem
                          key={spine.name}
                          disablePadding
                          disableGutters
                          sx={{ p: 0, m: 0, width: '100%' }}
                        >
                          <DeviceAlertRuleAccordion device={spine} user={user} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {showValves && valves.length > 0 && (
                  <>
                    <Typography
                      fontWeight={500}
                      color={theme.palette.text.secondary}
                      sx={{ my: 2, ml: 2 }}
                    >
                      {t('valveDevices')}
                    </Typography>

                    <List disablePadding sx={{ p: 0, m: 0 }}>
                      {valves.map((valve) => (
                        <ListItem
                          key={valve.name}
                          disablePadding
                          disableGutters
                          sx={{ p: 0, m: 0, width: '100%' }}
                        >
                          <ValveAlertRuleAccordion valve={valve} user={user} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {showRuuviTags && ruuviTags.length > 0 && (
                  <>
                    <Typography
                      fontWeight={500}
                      color={theme.palette.text.secondary}
                      sx={{ my: 2, ml: 2 }}
                    >
                      {t('conditionMonitoring')}
                    </Typography>
                    <List disablePadding sx={{ p: 0, m: 0 }}>
                      {ruuviTags.map((ruuviTag) => (
                        <ListItem
                          key={ruuviTag.id}
                          disablePadding
                          disableGutters
                          sx={{ p: 0, m: 0, width: '100%' }}
                        >
                          <RuuviTagAlertRuleAccordion ruuviTag={ruuviTag} user={user} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            ) : (
              <AlertRuleDataGrid
                alertRules={alertRules}
                alertTemplates={alertTemplates}
                project={project}
                device={device}
                ruuviTag={ruuviTag}
                user={user}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
